import React, { useState } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { InvitationService } from '../services/invitationService';
import { InputValidator } from '../utils/inputValidator';
import { SecurityMonitor } from '../services/securityMonitor';
import { AuditLogger } from '../services/auditLogger';
import { EmailVerificationService } from '../services/emailVerificationService';
import './Login.css';

interface LoginProps {
  onLogin: (user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showPresentation, setShowPresentation] = useState(false);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage('');
    setLoading(true);

    try {
      if (!InputValidator.isValidEmail(resetEmail)) {
        setResetMessage('Please enter a valid email address');
        setLoading(false);
        return;
      }

      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox.');
      console.log('✅ Password reset email sent to:', resetEmail);
      
      // Auto-close modal after 3 seconds
      setTimeout(() => {
        setShowPasswordReset(false);
        setResetMessage('');
        setResetEmail('');
      }, 3000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      setResetMessage(error.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Input validation
      if (!InputValidator.isValidEmail(email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      if (!InputValidator.isValidPassword(password)) {
        setError('Password must be at least 8 characters long');
        setLoading(false);
        return;
      }

      let userCredential;
      
      if (isSignUp) {
        // Validate invitation code before creating account
        if (!invitationCode.trim()) {
          setError('Invitation code is required to create an account');
          setLoading(false);
          return;
        }

        if (!InputValidator.isValidInvitationCode(invitationCode)) {
          setError('Invalid invitation code format');
          setLoading(false);
          return;
        }

        const validation = await InvitationService.validateCode(invitationCode.trim().toUpperCase(), email);
        
        if (!validation.valid) {
          // Monitor invalid invitation code usage
          await SecurityMonitor.monitorInvitationCodeUsage('anonymous', email, invitationCode, false);
          
          setError(validation.error || 'Invalid invitation code');
          setLoading(false);
          return;
        }

        // Create account
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Send email verification
        await EmailVerificationService.sendVerification(userCredential.user);
        console.log('✅ Verification email sent to:', email);
        
        // Mark invitation code as used and get user role
        // Use email username as display name for now (can be updated later in settings)
        const displayName = email.split('@')[0];
        const userRole = await InvitationService.markInvitationCodeAsUsed(
          invitationCode.trim().toUpperCase(), 
          userCredential.user.uid, 
          email,
          displayName
        );
        
        // Monitor successful invitation code usage
        await SecurityMonitor.monitorInvitationCodeUsage(userCredential.user.uid, email, invitationCode, true);
        
        // Log user registration
        await AuditLogger.logUserRegistration(userCredential.user.uid, email, invitationCode);
        
        // Store user role in localStorage for quick access
        localStorage.setItem('userRole', userRole);
        
        // Show verification message
        setError('Account created! Please check your email to verify your account before logging in.');
      } else {
        // Sign in (no invitation code needed for existing users)
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        // Monitor successful login
        await SecurityMonitor.monitorLoginAttempt(email, true, userCredential.user.uid);
      }
      
      onLogin(userCredential.user);
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Monitor failed login
      console.log('🔒 About to monitor failed login for:', email);
      await SecurityMonitor.monitorLoginAttempt(email, false);
      console.log('🔒 Failed login monitoring completed for:', email);
      
      setError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* About TEP Button - Top Right */}
      <button 
        className="about-tep-btn-top-right"
        onClick={() => setShowPresentation(true)}
        title="Learn about TEP"
      >
        ℹ️ About TEP
      </button>

      <div className="login-header">
        <img src="/logo.svg" alt="Trade Execution Platform Logo" className="login-logo" />
      </div>
      
      <div className="login-form-container">
        <form onSubmit={handleSubmit} className="login-form">
          <h2>{isSignUp ? 'Create Account' : 'Sign In'}</h2>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              minLength={6}
            />
          </div>
          
          {isSignUp && (
            <div className="form-group">
              <label htmlFor="invitationCode">Invitation Code:</label>
              <input
                type="text"
                id="invitationCode"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                required
                placeholder="TEP-XXXX-XXXX-XXXX"
                className="invitation-code-input"
              />
              <small className="help-text">
                🔒 Invitation code required for new accounts
              </small>
            </div>
          )}
          
          <button type="submit" disabled={loading} className="login-button">
            {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
          
          {!isSignUp && (
            <div className="forgot-password-link">
              <button
                type="button"
                onClick={() => setShowPasswordReset(true)}
                className="link-button"
              >
                Forgot Password?
              </button>
            </div>
          )}
          
          <div className="form-switch">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="switch-button"
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>
          </div>
        </form>
      </div>
      
      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="modal-overlay" onClick={() => setShowPasswordReset(false)}>
          <div className="modal-content password-reset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button 
                className="btn-close-modal"
                onClick={() => setShowPasswordReset(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handlePasswordReset} className="reset-form">
              <p className="reset-instructions">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  className="reset-email-input"
                />
              </div>
              
              {resetMessage && (
                <div className={`reset-message ${resetMessage.includes('sent') ? 'success' : 'error'}`}>
                  {resetMessage}
                </div>
              )}
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowPasswordReset(false)}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn-send-reset"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TEP Presentation Modal */}
      {showPresentation && (
        <div className="modal-overlay presentation-overlay" onClick={() => setShowPresentation(false)}>
          <div className="modal-content presentation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>About TEP - Trade Execution Platform</h2>
              <button 
                className="btn-close-modal"
                onClick={() => setShowPresentation(false)}
              >
                ×
              </button>
            </div>
            
            <div className="presentation-content">
              <iframe 
                src="/TEP_Professional_Presentation.html" 
                title="TEP Presentation"
                className="presentation-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
